import { useEffect, useState } from 'react';
import { format, subDays, subMonths } from 'date-fns';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, FileSearch, Trash2, Play, Pause, Clock, X, RefreshCw, Loader2, Pencil, Mail, AlertTriangle, CalendarIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { formatDateLocalized, formatRelativeTime } from '@/hooks/useLocationDetection';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useTrackActivity } from '@/hooks/useActivityTracker';

type DatePreset = 'week' | 'month' | '3months' | '6months' | 'custom' | '';

const DATE_PRESET_KEYS: DatePreset[] = ['week', 'month', '3months', '6months', 'custom'];

interface TedSearch {
  id: string;
  name: string;
  keywords: string[];
  cpv_codes: string[];
  countries: string[];
  min_value: number | null;
  max_value: number | null;
  is_active: boolean;
  last_run_at: string | null;
  created_at: string;
  client_id: string;
  include_expired: boolean;
  only_with_deadline: boolean;
  date_from: string | null;
  date_to: string | null;
  last_run_new_results: number | null;
  last_run_status: string | null;
}

interface AlertData {
  id: string;
  search_id: string;
  user_id: string;
  email_enabled: boolean;
  frequency: string;
}

import { EU_COUNTRIES, getCountryName } from '@/lib/countries';
import { useTranslation } from 'react-i18next';
import CpvSearch from '@/components/CpvSearch';

export default function Monitoring() {
  const [searches, setSearches] = useState<TedSearch[]>([]);
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningMonitoring, setRunningMonitoring] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSearch, setEditingSearch] = useState<TedSearch | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    keywords: '',
    cpv_codes: [] as string[],
    countries: [] as string[],
    min_value: '',
    max_value: '',
    emailEnabled: false,
    emailFrequency: 'daily',
    include_expired: false,
    only_with_deadline: false,
    date_from: undefined as Date | undefined,
    date_to: undefined as Date | undefined,
  });
  const [datePreset, setDatePreset] = useState<DatePreset>('');

  const handleDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    const today = new Date();
    
    switch (preset) {
      case 'week':
        setFormData(prev => ({ ...prev, date_from: subDays(today, 7), date_to: today }));
        break;
      case 'month':
        setFormData(prev => ({ ...prev, date_from: subMonths(today, 1), date_to: today }));
        break;
      case '3months':
        setFormData(prev => ({ ...prev, date_from: subMonths(today, 3), date_to: today }));
        break;
      case '6months':
        setFormData(prev => ({ ...prev, date_from: subMonths(today, 6), date_to: today }));
        break;
      case 'custom':
        break;
      default:
        setFormData(prev => ({ ...prev, date_from: undefined, date_to: undefined }));
    }
  };

  const clearDates = () => {
    setFormData(prev => ({ ...prev, date_from: undefined, date_to: undefined }));
    setDatePreset('');
  };
  const { toast } = useToast();
  const trackActivity = useTrackActivity();
  const { role, clientId, user } = useAuth();
  const { maxMonitorings, isSubscribed, emailAlertsEnabled } = useSubscription();
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'it';

  const isAtMonitoringLimit = maxMonitorings !== null && searches.length >= maxMonitorings;
  const canCreateMonitoring = !isAtMonitoringLimit;
  const isFreeUser = !isSubscribed;

  useEffect(() => {
    fetchSearches();
    fetchAlerts();
  }, [user]);

  const fetchSearches = async () => {
    try {
      const { data, error } = await supabase
        .from('ted_searches')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSearches(data || []);
    } catch (error) {
      console.error('Error fetching searches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setAlerts(data || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const getAlertForSearch = (searchId: string) => {
    return alerts.find(a => a.search_id === searchId);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      keywords: '',
      cpv_codes: [],
      countries: [],
      min_value: '',
      max_value: '',
      emailEnabled: false,
      emailFrequency: 'daily',
      include_expired: false,
      only_with_deadline: false,
      date_from: undefined,
      date_to: undefined,
    });
    setDatePreset('');
    setEditingSearch(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (search: TedSearch) => {
    setEditingSearch(search);
    const existingAlert = getAlertForSearch(search.id);
    setFormData({
      name: search.name,
      keywords: search.keywords.join(', '),
      cpv_codes: search.cpv_codes || [],
      countries: search.countries || [],
      min_value: search.min_value?.toString() || '',
      max_value: search.max_value?.toString() || '',
      emailEnabled: existingAlert?.email_enabled || false,
      emailFrequency: existingAlert?.frequency || 'daily',
      include_expired: search.include_expired || false,
      only_with_deadline: search.only_with_deadline || false,
      date_from: search.date_from ? new Date(search.date_from) : undefined,
      date_to: search.date_to ? new Date(search.date_to) : undefined,
    });
    // Determine preset based on existing dates
    setDatePreset(search.date_from || search.date_to ? 'custom' : '');
    setDialogOpen(true);
  };


  const handleCountryChange = (country: string) => {
    const countries = formData.countries.includes(country)
      ? formData.countries.filter((c) => c !== country)
      : [...formData.countries, country];
    setFormData({ ...formData, countries });
  };

  const handleSaveSearch = async () => {
    const hasKeywords = formData.keywords && formData.keywords.trim().length > 0;
    const hasCpvCodes = formData.cpv_codes && formData.cpv_codes.length > 0;
    const hasCountries = formData.countries && formData.countries.length > 0;
    
    if (!formData.name || (!hasKeywords && !hasCpvCodes && !hasCountries)) {
      toast({
        variant: "destructive",
        title: t('monitoring.requiredFields'),
        description: t('monitoring.requiredFieldsDescriptionExtended', 'Inserisci un nome e almeno: parole chiave, un codice CPV, oppure un paese.'),
      });
      return;
    }

    if (!clientId && !editingSearch) {
      toast({
        variant: "destructive",
        title: t('common.error', 'Errore'),
        description: t('monitoring.noClientError'),
      });
      return;
    }

    if (!user) {
      toast({
        variant: "destructive",
        title: t('common.error', 'Errore'),
        description: t('monitoring.notLoggedIn'),
      });
      return;
    }

    try {
      trackActivity(editingSearch ? 'settings_update' : 'monitoring_create', { name: formData.name, action: editingSearch ? 'edit_monitoring' : 'create_monitoring' });
      const searchData = {
        name: formData.name,
        keywords: formData.keywords.split(',').map((k) => k.trim()).filter(k => k),
        cpv_codes: formData.cpv_codes,
        countries: formData.countries,
        min_value: formData.min_value ? parseFloat(formData.min_value) : null,
        max_value: formData.max_value ? parseFloat(formData.max_value) : null,
        include_expired: formData.include_expired,
        only_with_deadline: formData.only_with_deadline,
        date_from: formData.date_from ? format(formData.date_from, 'yyyy-MM-dd') : null,
        date_to: formData.date_to ? format(formData.date_to, 'yyyy-MM-dd') : null,
      };

      let searchId = editingSearch?.id;

      if (editingSearch) {
        // Update existing
        const { error } = await supabase
          .from('ted_searches')
          .update(searchData)
          .eq('id', editingSearch.id);

        if (error) throw error;
      } else {
        // Create new
        const { data, error } = await supabase.from('ted_searches').insert({
          ...searchData,
          client_id: clientId,
        }).select().single();

        if (error) throw error;
        searchId = data.id;
      }

      // Handle alert configuration
      if (searchId) {
        const existingAlert = getAlertForSearch(searchId);
        
        if (formData.emailEnabled) {
          if (existingAlert) {
            // Update existing alert
            await supabase
              .from('alerts')
              .update({
                email_enabled: formData.emailEnabled,
                frequency: formData.emailFrequency,
              })
              .eq('id', existingAlert.id);
          } else {
            // Create new alert
            await supabase.from('alerts').insert({
              search_id: searchId,
              user_id: user.id,
              email_enabled: formData.emailEnabled,
              frequency: formData.emailFrequency,
            });
            trackActivity('alert_create', { search_id: searchId, frequency: formData.emailFrequency });
          }
        } else if (existingAlert) {
          // Disable alert
          await supabase
            .from('alerts')
            .update({ email_enabled: false })
            .eq('id', existingAlert.id);
          trackActivity('alert_toggle', { search_id: searchId, enabled: false });
        }
      }

      toast({
        title: editingSearch ? t('monitoring.monitoringUpdated') : t('monitoring.monitoringCreated'),
      });

      setDialogOpen(false);
      resetForm();
      fetchSearches();
      fetchAlerts();
    } catch (error: any) {
      console.error('Error saving search:', error);
      toast({
        variant: "destructive",
        title: t('common.error', 'Errore'),
        description: error.message,
      });
    }
  };

  const toggleSearchActive = async (search: TedSearch) => {
    try {
      const { error } = await supabase
        .from('ted_searches')
        .update({ is_active: !search.is_active })
        .eq('id', search.id);

      if (error) throw error;

      setSearches(
        searches.map((s) =>
          s.id === search.id ? { ...s, is_active: !s.is_active } : s
        )
      );

      trackActivity('monitoring_toggle', { search_id: search.id, name: search.name, active: !search.is_active });
      toast({
        title: search.is_active ? t('monitoring.monitoringDeactivated') : t('monitoring.monitoringActivated'),
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error', 'Errore'),
        description: error.message,
      });
    }
  };

  const deleteSearch = async () => {
    if (!deleteConfirmId) return;
    
    try {
      const { error } = await supabase
        .from('ted_searches')
        .delete()
        .eq('id', deleteConfirmId);

      if (error) throw error;

      const deletedSearch = searches.find(s => s.id === deleteConfirmId);
      setSearches(searches.filter((s) => s.id !== deleteConfirmId));
      trackActivity('monitoring_delete', { search_id: deleteConfirmId, name: deletedSearch?.name });
      toast({
        title: t('monitoring.monitoringDeleted'),
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error', 'Errore'),
        description: error.message,
      });
    } finally {
      setDeleteConfirmId(null);
    }
  };
  const runMonitoringManually = async () => {
    if (searches.filter(s => s.is_active).length === 0) {
      toast({
        variant: "destructive",
        title: t('monitoring.noActiveMonitoring'),
        description: t('monitoring.noActiveMonitoringDesc'),
      });
      return;
    }

    setRunningMonitoring(true);
    trackActivity('monitoring_run');
    try {
      // Call run-monitoring directly with user's auth token
      const { data, error } = await supabase.functions.invoke('run-monitoring', {
        body: { manual: true },
      });

      if (error) {
        throw new Error(error.message || t('monitoring.runErrorDesc'));
      }

      toast({
        title: t('monitoring.monitoringComplete'),
        description: t('monitoring.newTendersFound', { count: data?.newResults || 0 }),
      });
      
      fetchSearches(); // Refresh to show updated last_run_at
    } catch (error: any) {
      console.error('Error running monitoring:', error);
      toast({
        variant: "destructive",
        title: t('monitoring.runError'),
        description: error.message || t('monitoring.runErrorDesc'),
      });
    } finally {
      setRunningMonitoring(false);
    }
  };

  return (
    <DashboardLayout>
      <TooltipProvider>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">{t('monitoring.title')}</h1>
              <p className="text-muted-foreground mt-1">
                {t('monitoring.subtitle')}
              </p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge 
                  variant={isAtMonitoringLimit ? "destructive" : "secondary"} 
                  className="h-7 shrink-0 whitespace-nowrap px-3 text-[11px] font-semibold tabular-nums cursor-help"
                >
                  {searches.length} / {maxMonitorings === null ? '∞' : maxMonitorings}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('tooltips.monitoringCounter')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex gap-2">
            {searches.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    onClick={runMonitoringManually}
                    disabled={runningMonitoring}
                  >
                    {runningMonitoring ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {t('monitoring.runNow')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.runNow')}</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button onClick={openCreateDialog} disabled={isAtMonitoringLimit && !editingSearch}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('monitoring.newMonitoring')}
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.newMonitoring')}</p>
                </TooltipContent>
              </Tooltip>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingSearch ? t('monitoring.editMonitoring') : t('monitoring.createMonitoring')}
                </DialogTitle>
                <DialogDescription>
                  {editingSearch 
                    ? t('monitoring.configureEditDescription')
                    : t('monitoring.configureNewDescription')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('monitoring.monitoringName')} *</Label>
                  <Input
                    id="name"
                    placeholder={t('monitoring.monitoringNamePlaceholder')}
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="keywords">{t('search.keywords')} * ({t('common.commaSeparated')})</Label>
                  <Input
                    id="keywords"
                    placeholder={t('search.keywordsPlaceholder')}
                    value={formData.keywords}
                    onChange={(e) =>
                      setFormData({ ...formData, keywords: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('search.cpvCodes')}</Label>
                  <CpvSearch 
                    selectedCodes={formData.cpv_codes} 
                    onCodesChange={(codes) => setFormData({ ...formData, cpv_codes: codes })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('search.country')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {EU_COUNTRIES.map((c) => (
                      <Badge
                        key={c.code}
                        variant={
                          formData.countries.includes(c.code)
                            ? "default"
                            : "outline"
                        }
                        className="cursor-pointer"
                        onClick={() => handleCountryChange(c.code)}
                      >
                        {getCountryName(c.code, currentLang)}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minValue">{t('search.minValue')}</Label>
                    <Input
                      id="minValue"
                      type="number"
                      placeholder="0"
                      value={formData.min_value}
                      onChange={(e) =>
                        setFormData({ ...formData, min_value: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxValue">{t('search.maxValue')}</Label>
                    <Input
                      id="maxValue"
                      type="number"
                      placeholder="∞"
                      value={formData.max_value}
                      onChange={(e) =>
                        setFormData({ ...formData, max_value: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Date Range Section */}
                <div className="space-y-3">
                  <Label>{t('search.publicationDate', 'Data pubblicazione')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {DATE_PRESET_KEYS.map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        variant={datePreset === preset ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDatePreset(preset)}
                      >
                        {t(`search.datePresets.${preset}`, preset)}
                      </Button>
                    ))}
                    {(formData.date_from || formData.date_to) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearDates}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  {datePreset === 'custom' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm">{t('search.from', 'Da')}</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !formData.date_from && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.date_from ? format(formData.date_from, "dd/MM/yyyy") : t('search.selectDate', 'Seleziona data')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.date_from}
                              onSelect={(date) => setFormData(prev => ({ ...prev, date_from: date }))}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">{t('search.to', 'A')}</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !formData.date_to && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.date_to ? format(formData.date_to, "dd/MM/yyyy") : t('search.selectDate', 'Seleziona data')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.date_to}
                              onSelect={(date) => setFormData(prev => ({ ...prev, date_to: date }))}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  )}
                  
                  {datePreset && datePreset !== 'custom' && formData.date_from && formData.date_to && (
                    <p className="text-sm text-muted-foreground">
                      {format(formData.date_from, "dd/MM/yyyy")} - {format(formData.date_to, "dd/MM/yyyy")}
                    </p>
                  )}
                </div>
                
                {/* Advanced Filters Section */}
                <div className="space-y-4 pt-4 border-t">
                  <Label className="text-base font-medium">{t('search.advancedFilters', 'Filtri avanzati')}</Label>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-sm">{t('search.includeExpired', 'Includi scaduti')}</Label>
                        <p className="text-xs text-muted-foreground">
                          {t('search.includeExpiredDescription', 'Mostra anche bandi con scadenza passata')}
                        </p>
                      </div>
                      <Switch
                        checked={formData.include_expired}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, include_expired: checked })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-sm">{t('search.onlyWithDeadline', 'Solo con scadenza')}</Label>
                        <p className="text-xs text-muted-foreground">
                          {t('search.onlyWithDeadlineDescription', 'Mostra solo bandi con data di scadenza')}
                        </p>
                      </div>
                      <Switch
                        checked={formData.only_with_deadline}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, only_with_deadline: checked })
                        }
                      />
                    </div>
                  </div>
                </div>
                
                {/* Email Alert Section */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {t('monitoring.emailAlerts')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('monitoring.emailAlertsDescription')}
                      </p>
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Switch
                            checked={formData.emailEnabled}
                            onCheckedChange={(checked) => {
                              if (!emailAlertsEnabled && checked) {
                                toast({
                                  variant: "destructive",
                                  title: t('monitoring.freeEmailAlertsBlocked'),
                                  description: t('monitoring.freeEmailAlertsBlockedDescription'),
                                });
                                return;
                              }
                              setFormData({ ...formData, emailEnabled: checked });
                            }}
                            disabled={!emailAlertsEnabled}
                          />
                        </div>
                      </TooltipTrigger>
                      {!emailAlertsEnabled && (
                        <TooltipContent>
                          <p>{t('monitoring.freeEmailAlertsBlockedDescription')}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                  
                  {!emailAlertsEnabled && (
                    <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700 dark:text-amber-300">
                        {t('monitoring.freeEmailAlertsBlockedDescription')}{' '}
                        <Link to="/pricing" className="font-medium underline hover:no-underline">
                          {t('monitoring.upgradePlan')}
                        </Link>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {formData.emailEnabled && emailAlertsEnabled && (
                    <div className="space-y-2">
                      <Label>{t('monitoring.emailFrequency')}</Label>
                      <Select
                        value={formData.emailFrequency}
                        onValueChange={(value) =>
                          setFormData({ ...formData, emailFrequency: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('monitoring.selectFrequency')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">{t('monitoring.frequencyImmediate')}</SelectItem>
                          <SelectItem value="daily">{t('monitoring.frequencyDaily')}</SelectItem>
                          <SelectItem value="weekly">{t('monitoring.frequencyWeekly')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSaveSearch}>
                  {editingSearch ? t('monitoring.saveChanges') : t('monitoring.createMonitoring')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {isAtMonitoringLimit && (
          <Alert variant="destructive" className="border-orange-500/50 bg-orange-50 dark:bg-orange-950/20">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertTitle className="text-orange-800 dark:text-orange-200">
              {t('monitoring.limitReached')}
            </AlertTitle>
            <AlertDescription className="text-orange-700 dark:text-orange-300">
              {t('monitoring.limitReachedDescription', { count: maxMonitorings })}
              {' '}
              <Link to="/pricing" className="font-medium underline hover:no-underline">
                {t('monitoring.upgradePlan')}
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-0 shadow-sm animate-pulse">
                <CardContent className="p-6 h-48" />
              </Card>
            ))}
          </div>
        ) : searches.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <FileSearch className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                {t('monitoring.noMonitoring')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('monitoring.noMonitoringDescription')}
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                {t('monitoring.createMonitoring')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {searches.map((search) => (
              <Card key={search.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{search.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {search.keywords.slice(0, 3).join(', ')}
                        {search.keywords.length > 3 && ` +${search.keywords.length - 3}`}
                      </CardDescription>
                    </div>
                    <Switch
                      checked={search.is_active}
                      onCheckedChange={() => toggleSearchActive(search)}
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {search.cpv_codes?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {search.cpv_codes.slice(0, 2).map((cpv) => (
                          <Badge key={cpv} variant="secondary" className="text-xs">
                            {cpv}
                          </Badge>
                        ))}
                        {search.cpv_codes.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{search.cpv_codes.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}
                    {search.countries?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {search.countries.map((c) => (
                          <Badge key={c} variant="outline" className="text-xs">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {/* Expired date_to warning */}
                    {search.date_to && new Date(search.date_to) < new Date() && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded">
                            <AlertTriangle className="w-3 h-3" />
                            <span>{t('monitoring.dateExpired')}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t('monitoring.dateExpiredTooltip', { date: search.date_to })}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {/* Last run status */}
                    {search.last_run_status && (
                      <div className={cn(
                        "flex items-center gap-1.5 text-xs px-2 py-1 rounded",
                        search.last_run_new_results && search.last_run_new_results > 0
                          ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30"
                          : search.last_run_status.startsWith('Errore') || search.last_run_status.startsWith('Error')
                            ? "text-destructive bg-destructive/10"
                            : "text-muted-foreground bg-muted"
                      )}>
                        <FileSearch className="w-3 h-3" />
                        <span>
                          {search.last_run_new_results && search.last_run_new_results > 0
                            ? t('monitoring.newTendersFound', { count: search.last_run_new_results })
                            : search.last_run_status.startsWith('Errore') || search.last_run_status.startsWith('Error')
                              ? t('monitoring.runError')
                              : t('monitoring.noNewTendersFound')}
                        </span>
                      </div>
                    )}
                    {/* Email Alert Badge */}
                    {getAlertForSearch(search.id)?.email_enabled && (
                      <div className="flex items-center gap-1.5 text-xs text-primary">
                        <Mail className="w-3 h-3" />
                        <span>{t('monitoring.emailEnabled')}</span>
                      </div>
                    )}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground" title={search.last_run_at ? formatDateLocalized(search.last_run_at, { includeTime: true }) : undefined}>
                          <Clock className="w-3 h-3" />
                          {search.last_run_at
                            ? formatRelativeTime(search.last_run_at)
                            : t('monitoring.neverRun')}
                      </div>
                      <div className="flex gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(search)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t('tooltips.editMonitoring')}</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setDeleteConfirmId(search.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t('tooltips.deleteMonitoring')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('monitoring.deleteConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('monitoring.deleteConfirmDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={deleteSearch} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      </TooltipProvider>
    </DashboardLayout>
  );
}
